import LocalizedLink from "@/components/LocalizedLink";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import xLogoDark from "@/assets/x-logo-dark.webp";
import HeroFlowLoop from "./HeroFlowLoop";

export function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden bg-black">
      {/* Mobile-only simple gradient */}
      <div
        className="absolute inset-x-0 bottom-0 h-[55%] md:hidden pointer-events-none"
        style={{
          zIndex: 1,
          background: "radial-gradient(ellipse at 50% 90%, hsl(261 72% 64% / 0.35), transparent 65%)",
          filter: "blur(40px)",
        }}
      />

      {/* Aurora glow effect - desktop only */}
      <div className="absolute inset-x-0 bottom-0 h-[70%] pointer-events-none hidden md:block" style={{ zIndex: 1 }}>
        <div
          className="absolute rounded-full"
          style={{
            width: "700px", height: "500px",
            background: "radial-gradient(ellipse, hsl(200 95% 60% / 0.55), hsl(210 90% 50% / 0.22), transparent 70%)",
            bottom: "5%", left: "5%", filter: "blur(100px)",
            animation: "aurora-pulse-1 12s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "600px", height: "450px",
            background: "radial-gradient(ellipse, hsl(270 90% 65% / 0.6), hsl(280 85% 55% / 0.28), transparent 70%)",
            bottom: "0%", left: "25%", filter: "blur(90px)",
            animation: "aurora-pulse-2 15s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "550px", height: "400px",
            background: "radial-gradient(ellipse, hsl(320 85% 65% / 0.55), hsl(330 80% 55% / 0.22), transparent 70%)",
            bottom: "10%", left: "45%", filter: "blur(80px)",
            animation: "aurora-pulse-3 10s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "500px", height: "380px",
            background: "radial-gradient(ellipse, hsl(230 95% 55% / 0.5), hsl(240 90% 45% / 0.18), transparent 70%)",
            bottom: "15%", left: "65%", filter: "blur(75px)",
            animation: "aurora-pulse-4 18s ease-in-out infinite",
          }}
        />
      </div>

      {/* Dark X Logo - decorative */}
      <div
        className="absolute inset-x-0 flex justify-center pointer-events-none bottom-[-15%] md:bottom-[-55%] animate-[hero-fade-in_1.8s_ease-out_0.3s_both]"
        style={{ zIndex: 5 }}
      >
        <img
          src={xLogoDark}
          alt=""
          width={900}
          height={440}
          loading="lazy"
          decoding="async"
          className="w-[420px] sm:w-[500px] md:w-[1000px] lg:w-[1300px] xl:w-[1700px] select-none opacity-90"
          style={{
            filter: "brightness(0) saturate(100%)",
            maskImage: "linear-gradient(to bottom, transparent 0%, black 40%, black 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 40%, black 100%)",
          }}
        />
      </div>

      {/* Hero entrance keyframes */}
      <style>{`
        @keyframes hero-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes hero-fade-down {
          from { opacity: 0; transform: translateY(-24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-fade-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-fade-right {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Main Content */}
      <div className="relative pt-28 md:pt-36 pb-24" style={{ zIndex: 10 }}>
        <div className="container max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-12 items-center">

            {/* Left: copy */}
            <div className="text-center lg:text-left">
              {/* Eyebrow badge removed per request */}

              {/* Main headline */}
              <h1 className="text-[2rem] sm:text-4xl md:text-5xl lg:text-[3.25rem] xl:text-[3.75rem] font-light text-white mb-5 leading-[1.2] tracking-tight animate-[hero-fade-down_0.8s_ease-out_0.1s_both] pb-2">
                <span className="lg:hidden">
                  {t("hero.titleMobileLine1")}<br />
                  {t("hero.titleMobileLine2")} <span className="gradient-text-animated font-normal pb-1 inline-block">{t("hero.titleMobileSubLine")}</span>
                </span>
                <span className="hidden lg:inline">
                  {t("hero.title")}<br />
                  <span className="gradient-text-animated font-normal animate-[hero-fade-up_0.8s_ease-out_0.35s_both] inline-block pb-1">{t("hero.titleSub")}</span>
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-white/65 text-sm sm:text-base md:text-lg max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed px-2 sm:px-0 animate-[hero-fade-up_0.7s_ease-out_0.45s_both] font-light">
                {t("hero.subtitle")}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 mb-4 px-4 sm:px-0 animate-[hero-fade-up_0.7s_ease-out_0.6s_both]">
                <LocalizedLink
                  to="/platform/voice-ai"
                  className="group w-full sm:w-auto inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium rounded-full bg-white text-black hover:bg-white/90 transition-all"
                >
                  {t("hero.exploreVoiceAI")}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </LocalizedLink>
                <LocalizedLink
                  to="/schedule"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium rounded-full bg-transparent border border-white/20 text-white hover:bg-white/5 hover:border-white/30 transition-all"
                >
                  {t("hero.scheduleCall")}
                </LocalizedLink>
              </div>
            </div>

            {/* Right: animated flow loop */}
            <div className="animate-[hero-fade-right_0.9s_ease-out_0.5s_both]">
              <HeroFlowLoop />
            </div>
          </div>
        </div>

        {/* Spacer for X logo visual */}
        <div className="h-12 md:h-20" />
      </div>
    </section>
  );
}
